import { Link } from "react-router";
import type { Product } from "@/lib/api.ts";
import { WishlistButton } from "./WishlistButton.tsx";
import { formatPrice, stripHtml } from "@/lib/utils.ts";

export function ProductCard({ product }: { product: Product }) {
  return (
    <div className="group">
      <Link to={`/product/${product.slug}`}>
        <div className="relative aspect-square overflow-hidden border border-transparent bg-bg-alt transition-all duration-300 group-hover:border-border group-hover:shadow-md">
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.name}
              className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-text-muted">
              No image
            </div>
          )}
        </div>
      </Link>
      <div className="pt-4">
        {product.category && (
          <span className="text-xs uppercase tracking-wide text-text-muted">
            {product.category}
          </span>
        )}
        <div className="mt-1 flex items-start justify-between gap-2">
          <h3 className="text-[15px] font-medium leading-snug text-heading">
            <Link to={`/product/${product.slug}`} className="hover:underline">
              {product.name}
            </Link>
          </h3>
          <WishlistButton productId={product.id} />
        </div>
        {product.richDesc && (() => {
          let text = stripHtml(product.richDesc);
          // Strip leading "ProductName is/are..." — the name is already shown above
          const nameWords = product.name.toLowerCase().split(/\s+/).filter(w => w.length >= 3);
          // Check if text before "is"/"are" contains a name word
          const connectorMatch = text.match(/^(.{2,60}?)\s+(?:is|are)\s+/i);
          if (connectorMatch) {
            const before = connectorMatch[1].toLowerCase();
            const overlap = nameWords.some(w => before.includes(w));
            if (overlap) {
              const rest = text.slice(connectorMatch[0].length);
              if (rest.length > 20) text = rest.replace(/^[a-z]/, c => c.toUpperCase());
            }
          }
          // Also handle "Name verb..." (e.g. "ThePhotoStick® OMNI automatically finds...")
          if (text === stripHtml(product.richDesc)) {
            const escaped = product.name.split(/\s+/).map(w => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("[\\s®™-]*");
            const nameStart = new RegExp(`^(the\\s+)?${escaped}[®™]*\\s+`, "i");
            const m = text.match(nameStart);
            if (m) {
              const rest = text.slice(m[0].length);
              if (rest.length > 20) text = rest.replace(/^[a-z]/, c => c.toUpperCase());
            }
          }
          return (
            <p className="mt-1 text-xs leading-relaxed text-text-muted line-clamp-2">
              {text}
            </p>
          );
        })()}
        {product.price && (
          <p className="mt-1.5 text-[15px] font-semibold text-heading">
            {formatPrice(product.price)}
          </p>
        )}
      </div>
    </div>
  );
}
